import { useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import { isArray, isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import { BarsLoadingIndicator } from 'components/common/barsLoadingIndicator';
import { Input } from '../common/input';
import { Modal } from 'components/common/modal';
import { closeModal } from 'features/modalSlice';
import PropTypes from 'prop-types';
import { apiConfig, COOKIE_NAME } from '../../config';
import { useGetBookingData } from 'hooks';
import { getCookie, isMobileDeviceRequest } from 'lib';
import { Dropdown } from 'components/common/dropdown';
import { noReCaptchaPublicKey } from '../../constants';
import './Click2CallModal.css';

const Click2CallModal = ({ closeOnOutsideClick = true }) => {
    const dispatch = useDispatch();
    const mobileRequest = isMobileDeviceRequest();
    const Lang = useSelector((state) => state.miniBookingWidget?.data?.Lang);
    const propertyID = useSelector(
        (state) => state.miniBookingWidget?.data?.propertyID
    );
    const click2CallData = useSelector(
        (state) => state.miniBookingWidget?.data?.click2CallDropDowns
    );
    const click2CallMessage = useSelector(
        (state) =>
            state.miniBookingWidget?.data?.click2Call?.click2CallMessage ||
            Lang?.click2CallText
    );
    const click2CallTermsAndConditions = useSelector(
        (state) =>
            state.miniBookingWidget?.data?.click2Call
                ?.click2CallTermsAndConditions
    );
    const dropdownOptions = isArray(click2CallData)
        ? click2CallData.map((item) => {
              return {
                  dropdownText: item?.click2CallName,
                  dropdownSelected: item?.dropdownSelected,
                  dropdownImg: item?.click2CallImage,
                  dropdownExtraData: {
                      countryPhoneCode: item?.click2CallCountryPhone,
                      countryCode: item?.click2CallCountryCode,
                  },
                  dropdownValue: item?.dropdownValue,
              };
          })
        : [];

    const reCaptchaRef = useRef(null);
    const [fullName, setFullName] = useState('');
    const [errors, setErrors] = useState({});
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [reCaptchaValue, setReCaptchaValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [countryPhoneCode, setCountryPhoneCode] = useState(
        isArray(click2CallData) ? click2CallData[0].click2CallCountryPhone : ''
    );
    const [phoneNumber, setPhoneNumber] = useState('');
    const [country, setCountry] = useState(
        isArray(click2CallData) ? click2CallData[0].click2CallCountryCode : ''
    );

    const reCapatchPublicKey =
        process.env.NODE_ENV === 'production'
            ? useSelector(
                  (state) => state.miniBookingWidget?.data?.reCaptchaPublicKey
              )
            : process.env.REACT_APP_RE_CAPTCHA_PUBLIC_KEY;

    const ignoreReCaptch = !reCapatchPublicKey;

    const headerText = useSelector(
        (state) =>
            state.miniBookingWidget?.data?.click2Call?.headerText ||
            Lang?.click2Call
    );
    const primaryColor = useSelector(
        (state) =>
            state.miniBookingWidget?.data?.click2Call?.primaryColor || 'ffffff'
    );
    const secondaryColor = useSelector(
        (state) =>
            state.miniBookingWidget?.data?.click2Call?.secondaryColor ||
            '00427e'
    );

    const colors = {
        backgroundColor: '#' + secondaryColor,
        color: '#' + primaryColor,
    };

    const isOpen = useSelector((state) => state.modal.isClick2CallModalOpen);
    const [modalContent, setModalContent] = useState('initial');

    const handleModalClose = useCallback(() => {
        dispatch(closeModal({ modalId: 'Click2CallModal' }));
        setErrors({});
        setFullName('');
        setCountryPhoneCode(click2CallData[0].click2CallCountryPhone);
        setReCaptchaValue('');
        setCountry(click2CallData[0].click2CallCountryCode);
        setAcceptedTerms(false);
        setPhoneNumber('');
    });

    const isValidPhoneNumber = (phone) => {
        const phoneRegex = /^\+?\d{10,15}$/;
        return phoneRegex.test(phone);
    };

    const { userBookingData } = useGetBookingData();

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errors = validateInputs();

        const token = reCapatchPublicKey
            ? reCaptchaRef.current.getValue()
            : noReCaptchaPublicKey;

        if (!isEmpty(errors)) {
            setErrors(errors);
            return;
        }

        const cookie = getCookie(COOKIE_NAME);

        try {
            setIsLoading(true);
            const response = await axios.post(apiConfig?.click2Call, {
                ...userBookingData,
                lang: userBookingData?.localeLanguage,
                fullName,
                phone: countryPhoneCode + phoneNumber,
                token,
                cookie,
                propertyID,
                timeofday: Date.now(),
                country,
            });

            if (response?.data === true) {
                toast.success(Lang?.successMessage, {
                    autoClose: true,
                    theme: 'colored',
                });
                setModalContent('success');
            }
        } catch (error) {
            toast.error(Lang?.networkError, {
                autoClose: true,
                theme: 'colored',
            });
            handleModalClose();
        }

        setIsLoading(false);

        if (reCaptchaRef.current) {
            reCaptchaRef.current.reset();
        }
    };

    const validateInputs = () => {
        const errors = {};

        if (!fullName) {
            errors.fullName = Lang?.requiredField;
        }

        if (
            !phoneNumber ||
            !isValidPhoneNumber(countryPhoneCode + phoneNumber)
        ) {
            errors.phone = Lang?.requiredField;
        }

        if (!ignoreReCaptch && !reCaptchaValue) {
            errors.reCaptcha = Lang?.pleaseCompleteRecaptcha;
        }

        if (click2CallTermsAndConditions && !acceptedTerms) {
            errors.terms =
                Lang?.mustAcceptTerms ||
                'You must accept the terms and conditions in order to continue.';
        }

        return errors;
    };

    const renderContent = () => {
        return (
            <div className="ab-call-us-cnt ab-container">
                {modalContent === 'success' ? (
                    <div className="thank-you-message">
                        <div className="thank-you-icon">
                            <span role="img" aria-label="check mark">
                                ✔️
                            </span>
                        </div>
                        {Lang?.thankYouForYourInterestWellContact}
                        <button
                            className="go-further-button"
                            onClick={handleModalClose}
                            style={{
                                backgroundColor: colors?.backgroundColor,
                                color: colors?.color,
                            }}
                        >
                            {Lang?.close}
                        </button>
                    </div>
                ) : (
                    <>
                        <h5 className="ab-call-us-heading-text">
                            {click2CallMessage}
                        </h5>
                        <form
                            className="ab-call-us-form ab-form"
                            onSubmit={handleSubmit}
                            noValidate
                        >
                            <div className="ab-input-group">
                                <div className="ab-input-field-cnt">
                                    <Input
                                        type="text"
                                        placeholder={Lang?.fullName}
                                        value={fullName}
                                        onChange={(event) =>
                                            setFullName(event.target.value)
                                        }
                                        isInvalid={Boolean(errors?.fullName)}
                                        errorMsg={errors.fullName}
                                        isRequired
                                        id="call-us-full-name"
                                        autoFocus
                                        label={Lang?.fullName}
                                    />
                                </div>
                            </div>

                            <div className="ab-input-group">
                                <div className="ab-input-field-cnt">
                                    {dropdownOptions.length > 0 && (
                                        <Dropdown
                                            align="left"
                                            noSemicolon
                                            onChange={(option) => {
                                                setCountryPhoneCode(
                                                    option?.dropdownExtraData
                                                        ?.countryPhoneCode
                                                );
                                                setCountry(
                                                    option?.dropdownExtraData
                                                        ?.countryCode
                                                );
                                                setPhoneNumber('');
                                            }}
                                            options={dropdownOptions}
                                            showTextWhenSelected={false}
                                            width="210px"
                                            extraClass="ab-mr-10"
                                        />
                                    )}
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Input
                                            type="text"
                                            value={countryPhoneCode}
                                            readOnly
                                            className="country-phone-code"
                                        />
                                        <Input
                                            type="text"
                                            placeholder={Lang?.phone}
                                            value={phoneNumber}
                                            onChange={(event) =>
                                                setPhoneNumber(
                                                    event.target.value
                                                )
                                            }
                                            isInvalid={Boolean(errors?.phone)}
                                            errorMsg={errors?.phone}
                                            isRequired
                                            id="call-us-phone"
                                        />
                                    </div>
                                </div>
                            </div>
                            {!!reCapatchPublicKey && (
                                <div className="ab-recaptcha-cnt">
                                    <ReCAPTCHA
                                        sitekey={reCapatchPublicKey}
                                        onChange={(value) =>
                                            setReCaptchaValue(value)
                                        }
                                        ref={reCaptchaRef}
                                    />
                                    {errors.reCaptcha && (
                                        <div className="ab-input-error-msg ab-input-error-msg--recaptcha">
                                            {errors.reCaptcha}
                                        </div>
                                    )}
                                </div>
                            )}
                            {click2CallTermsAndConditions && (
                                <div className="ab-input-group ab-mt-10">
                                    <label className="ab-flex ab-items-start">
                                        <input
                                            type="checkbox"
                                            checked={acceptedTerms}
                                            onChange={(e) =>
                                                setAcceptedTerms(
                                                    e.target.checked
                                                )
                                            }
                                            aria-required="true"
                                        />
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: click2CallTermsAndConditions,
                                            }}
                                        />
                                    </label>
                                    {errors?.terms && (
                                        <div className="ab-input-error-msg">
                                            {errors.terms}
                                        </div>
                                    )}
                                </div>
                            )}

                            <button
                                className="ab-click-2-call-btn"
                                type="submit"
                                style={{
                                    backgroundColor: colors?.backgroundColor,
                                    color: colors?.color,
                                }}
                                disabled={isLoading || !acceptedTerms}
                            >
                                {isLoading ? (
                                    <BarsLoadingIndicator />
                                ) : (
                                    Lang?.submit
                                )}
                            </button>
                        </form>
                    </>
                )}
            </div>
        );
    };

    return (
        <Modal
            className="ab-modal--contact-us"
            color={colors?.color}
            backgroundColor={colors?.backgroundColor}
            isOpen={isOpen}
            onClose={handleModalClose}
            onOverlayClick={handleModalClose}
            closeOnOutsideClick={closeOnOutsideClick}
            placement={mobileRequest ? 'top' : 'center'}
            renderContent={renderContent}
            styles={{
                closeIcon: { color: colors?.primaryTextColor },
            }}
            title={headerText}
            variant="secondary"
            width={400}
        />
    );
};

Click2CallModal.propTypes = {
    closeOnOutsideClick: PropTypes.bool,
};

export default Click2CallModal;
